import { useState, type KeyboardEvent } from "react";
import { Button, Stack, Text, TextInput } from "@mantine/core";

export type SarahGuestContactValues = {
  name: string;
  phone: string;
  email: string;
};

export function formatSarahGuestContactMessage(values: SarahGuestContactValues): string {
  return `Name: ${values.name.trim()}\nPhone: ${values.phone.trim()}\nEmail: ${values.email.trim()}`;
}

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export default function SarahGuestContactForm({
  disabled,
  onSubmit,
}: {
  disabled?: boolean;
  onSubmit: (values: SarahGuestContactValues) => void;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    const values = {
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim(),
    };
    if (!values.name) {
      setError("Please enter your name.");
      return;
    }
    const digits = values.phone.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 15) {
      setError("Please enter a valid contact number.");
      return;
    }
    if (!EMAIL_RE.test(values.email)) {
      setError("Please enter a valid email.");
      return;
    }
    setError("");
    onSubmit(values);
  };

  const onFieldKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Stack
      gap={8}
      p={8}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: 10,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      <Text size="xs" c="#334155" lh={1.4} style={{ fontSize: 12 }}>
        Share your details — a GTsetu admin will connect you shortly. You can create an account on GTsetu to Explore more.
      </Text>
      <TextInput
        size="xs"
        label="Name"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        onKeyDown={onFieldKeyDown}
        disabled={disabled}
        styles={{ label: { fontSize: 11, fontWeight: 600, color: "#0F172A" } }}
      />
      <TextInput
        size="xs"
        label="Contact number"
        placeholder="+91 98765 43210"
        value={phone}
        onChange={(e) => setPhone(e.currentTarget.value)}
        onKeyDown={onFieldKeyDown}
        disabled={disabled}
        styles={{ label: { fontSize: 11, fontWeight: 600, color: "#0F172A" } }}
      />
      <TextInput
        size="xs"
        type="email"
        label="Email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
        onKeyDown={onFieldKeyDown}
        disabled={disabled}
        styles={{ label: { fontSize: 11, fontWeight: 600, color: "#0F172A" } }}
      />
      {error ? (
        <Text size="xs" c="red" style={{ fontSize: 11 }}>
          {error}
        </Text>
      ) : null}
      <Button
        size="compact-xs"
        radius="md"
        fullWidth
        disabled={disabled}
        onClick={submit}
        styles={{
          root: {
            background: "#04305c",
            fontWeight: 600,
            "&:hover": { background: "#032447" },
          },
        }}
      >
        Submit
      </Button>
    </Stack>
  );
}
